/* Atelier live search for the mobile menu drawer (snippets/atelier-menu-drawer.liquid).
   <atelier-predictive-search>: debounced Shopify predictive search (GET {routes.predictive_search_url}.json)
   for query suggestions, products, pages and articles, rendered under the field as a combobox + listbox
   (arrow keys move aria-activedescendant, Enter opens the active row, Escape closes the list), with loading,
   no-results and "Search for “…”" rows and a polite status message for screen readers. */
(() => {
  if (customElements.get('atelier-predictive-search')) return;

  const DEBOUNCE_MS = 300;
  const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ESCAPES[c]);
  const reEscape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Wrap each occurrence of the typed words in <mark> (text is escaped first).
  const highlight = (text, query) => {
    const words = query.split(/\s+/).filter(Boolean).map(reEscape);
    if (!words.length) return esc(text);
    const re = new RegExp(`(${words.join('|')})`, 'gi');
    return String(text)
      .split(re)
      .map((part, i) => (i % 2 ? `<mark>${esc(part)}</mark>` : esc(part)))
      .join('');
  };

  const sized = (url, width) => {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${width}`;
  };

  // Shopify money formats ({{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}}, ...).
  const formatAmount = (cents, format) => {
    const group = (n, decimals, thousands = ',', decimal = '.') => {
      const [whole, part] = (n / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (part ? decimal + part : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const type = match ? match[1] : 'amount';
    let value;
    switch (type) {
      case 'amount_no_decimals':
        value = group(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = group(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = group(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = group(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = group(cents, 2, ' ', '.');
        break;
      default:
        value = group(cents, 2);
    }
    return match ? format.replace(match[0], value) : value;
  };

  let uid = 0;

  class AtelierPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-at-search-panel]');
      this.status = this.querySelector('[data-at-search-status]');
      this.clearButton = this.querySelector('[data-at-search-clear]');
      this.drawer = this.closest('[data-at-drawer]');
      if (!this.form || !this.input) return;

      this.enabled = this.dataset.enabled === 'true' && !!this.panel && 'fetch' in window;
      this.cache = new Map();
      this.term = '';
      this.uid = `AtelierPS${(uid += 1)}`;
      this.icons = {};
      this.querySelectorAll('template[data-at-search-icon]').forEach((t) => {
        this.icons[t.dataset.atSearchIcon] = t.innerHTML.trim();
      });

      this.onInput = () => this.handleInput();
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onFocus = () => {
        if (this.enabled && this.input.value.trim() && this.panel.innerHTML) this.open();
      };
      this.onClear = () => {
        this.input.value = '';
        this.handleInput();
        this.input.focus();
      };
      // Enter in the field submits the form: open the highlighted row instead when it is a link; with no
      // row highlighted (or the "Search for" row) the form goes to the search page as usual.
      this.onSubmit = (event) => {
        const option = this.activeOption();
        const link = option && option.querySelector('a[href]');
        if (link) {
          event.preventDefault();
          link.click();
        }
      };
      this.onPanelPointer = (event) => {
        if (event.pointerType !== 'mouse') return;
        const option = event.target.closest('[role="option"]');
        if (option) this.setActive(option, false);
      };

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('focus', this.onFocus);
      this.form.addEventListener('submit', this.onSubmit);
      if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);
      if (this.panel) this.panel.addEventListener('pointermove', this.onPanelPointer);
      // The browser may restore a typed term on back/forward navigation.
      if (this.input.value) this.handleInput();
    }

    disconnectedCallback() {
      if (!this.input) return;
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onKeydown);
      this.input.removeEventListener('focus', this.onFocus);
      this.form.removeEventListener('submit', this.onSubmit);
      if (this.clearButton) this.clearButton.removeEventListener('click', this.onClear);
      if (this.panel) this.panel.removeEventListener('pointermove', this.onPanelPointer);
      this.setSearching(false);
    }

    label(name, values = {}) {
      let text = this.dataset[`label${name}`] || '';
      Object.entries(values).forEach(([key, value]) => {
        text = text.split(`[${key}]`).join(value);
      });
      return text;
    }

    handleInput() {
      const raw = this.input.value;
      const term = raw.trim();
      if (this.clearButton) this.clearButton.hidden = raw.length === 0;
      if (!this.enabled) return;
      clearTimeout(this.timer);

      if (!term) {
        this.term = '';
        if (this.controller) this.controller.abort();
        this.setLoading(false);
        this.panel.innerHTML = '';
        this.close();
        this.announce('');
        return;
      }

      // Show the panel at once (the menu rows step aside), then search when typing pauses.
      if (!this.panel.innerHTML) this.renderLoading();
      this.open();
      this.setLoading(true);
      this.timer = setTimeout(() => this.search(term), DEBOUNCE_MS);
    }

    search(term) {
      const key = term.toLowerCase().replace(/\s+/g, ' ');
      this.term = term;
      if (this.cache.has(key)) {
        this.render(term, this.cache.get(key));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.announce(this.label('Loading'));

      const base = this.dataset.url || '/search/suggest';
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'query,product,page,article',
        'resources[limit]': this.dataset.limit || '4',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      fetch(`${base}.json?${params.toString()}`, {
        signal: this.controller.signal,
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          const results = (data && data.resources && data.resources.results) || {};
          this.cache.set(key, results);
          if (this.input.value.trim() === term) this.render(term, results);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          if (this.input.value.trim() === term) this.render(term, {});
        });
    }

    money(amount) {
      const cents = Math.round(parseFloat(amount) * 100);
      if (!Number.isFinite(cents)) return '';
      const currency = this.dataset.currency;
      if (currency && this.dataset.shopCurrency && currency !== this.dataset.shopCurrency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency,
          }).format(cents / 100);
        } catch (e) {
          /* fall through to the shop format */
        }
      }
      return formatAmount(cents, this.dataset.moneyFormat || '${{amount}}');
    }

    price(product) {
      if (this.dataset.showPrice !== 'true') return '';
      const min = parseFloat(product.price_min || product.price);
      const max = parseFloat(product.price_max || product.price);
      if (!Number.isFinite(min)) return '';
      if (max > min) {
        const from = this.dataset.from ? `${esc(this.dataset.from)} ` : '';
        return `<span class="at-psearch__price">${from}${esc(this.money(min))}</span>`;
      }
      const compare = parseFloat(product.compare_at_price_max);
      const was =
        compare > min
          ? ` <s class="at-psearch__was"><span class="at-visually-hidden">${esc(this.dataset.labelRegular || '')}</span>${esc(
              this.money(compare)
            )}</s>`
          : '';
      return `<span class="at-psearch__price">${esc(this.money(min))}${was}</span>`;
    }

    option(inner, href, extraClass = '') {
      this.count += 1;
      return `<li class="at-psearch__opt" role="option" id="${this.uid}-${this.count}" aria-selected="false"><a class="at-psearch__row${extraClass}" href="${esc(href)}" tabindex="-1">${inner}</a></li>`;
    }

    group(name, label, items) {
      if (!items.length) return '';
      return `<p class="at-psearch__heading" aria-hidden="true">${esc(label)}</p><ul class="at-psearch__group at-psearch__group--${name}" role="group" aria-label="${esc(label)}">${items.join('')}</ul>`;
    }

    thumb(url, alt) {
      if (!url) return `<span class="at-psearch__thumb at-psearch__thumb--icon" aria-hidden="true">${this.icons.page || ''}</span>`;
      return `<span class="at-psearch__thumb" aria-hidden="true"><img src="${esc(sized(url, 120))}" alt="${esc(alt || '')}" width="56" height="56" loading="lazy"></span>`;
    }

    render(term, results) {
      this.count = 0;
      const queries = (results.queries || []).filter((q) => q.text && q.text.toLowerCase() !== term.toLowerCase());
      const products = results.products || [];
      const pages = results.pages || [];
      const articles = results.articles || [];
      const chevron = this.icons.chevron || '';

      const html = [
        this.group(
          'queries',
          this.label('Queries'),
          queries.map((q) =>
            this.option(
              `<span class="at-psearch__qicon" aria-hidden="true">${this.icons.search || ''}</span><span class="at-psearch__qtext">${highlight(q.text, term)}</span>`,
              q.url,
              ' at-psearch__row--query'
            )
          )
        ),
        this.group(
          'products',
          this.label('Products'),
          products.map((p) => {
            const image = (p.featured_image && p.featured_image.url) || p.image;
            const alt = (p.featured_image && p.featured_image.alt) || '';
            return this.option(
              `${this.thumb(image, alt)}<span class="at-psearch__text"><span class="at-psearch__title">${esc(p.title)}</span>${this.price(p)}</span>${chevron}`,
              p.url
            );
          })
        ),
        this.group(
          'pages',
          this.label('Pages'),
          pages.map((p) =>
            this.option(
              `${this.thumb('', '')}<span class="at-psearch__text"><span class="at-psearch__title">${esc(p.title)}</span></span>${chevron}`,
              p.url
            )
          )
        ),
        this.group(
          'articles',
          this.label('Articles'),
          articles.map((a) => {
            const image = (a.featured_image && a.featured_image.url) || a.image;
            return this.option(
              `${this.thumb(image, '')}<span class="at-psearch__text"><span class="at-psearch__title at-psearch__title--article">${esc(a.title)}</span></span>${chevron}`,
              a.url
            );
          })
        ),
      ].join('');

      const total = queries.length + products.length + pages.length + articles.length;
      const searchFor = esc(this.label('SearchFor', { terms: term }));
      const emptyText = this.label('Empty', { terms: term });
      this.count += 1;
      const all = `<ul class="at-psearch__group at-psearch__group--all" role="group" aria-label="${searchFor}"><li class="at-psearch__opt" role="option" id="${this.uid}-${this.count}" aria-selected="false"><button type="submit" class="at-psearch__row at-psearch__row--all" tabindex="-1"><span>${searchFor}</span>${chevron}</button></li></ul>`;
      const empty = total ? '' : `<p class="at-psearch__empty" aria-hidden="true">${esc(emptyText)}</p>`;

      this.panel.innerHTML = empty + html + all;
      this.panel.dataset.state = total ? 'results' : 'empty';
      this.setActive(null);
      this.setLoading(false);
      this.open();

      if (total) {
        const message = this.label(total === 1 ? 'CountOne' : 'CountOther', { count: total, terms: term });
        this.announce(message);
      } else {
        this.announce(emptyText);
      }
    }

    renderLoading() {
      this.panel.innerHTML = `<p class="at-psearch__loading" aria-hidden="true"><span class="at-psearch__dot"></span>${esc(
        this.label('Loading')
      )}</p>`;
      this.panel.dataset.state = 'loading';
    }

    setLoading(on) {
      this.classList.toggle('is-loading', on);
      if (this.panel) this.panel.setAttribute('aria-busy', String(on));
    }

    announce(text) {
      if (!this.status) return;
      // Re-set so the same message is read again after a new query.
      this.status.textContent = '';
      if (text) setTimeout(() => (this.status.textContent = text), 50);
    }

    open() {
      if (!this.enabled) return;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setSearching(true);
    }

    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.setActive(null);
      this.setSearching(false);
    }

    isOpen() {
      return !!this.panel && !this.panel.hidden;
    }

    setSearching(on) {
      this.classList.toggle('is-open', on);
      if (this.drawer) this.drawer.classList.toggle('is-searching', on);
    }

    options() {
      return this.panel ? [...this.panel.querySelectorAll('[role="option"]')] : [];
    }

    activeOption() {
      return this.panel && this.isOpen() ? this.panel.querySelector('[role="option"][aria-selected="true"]') : null;
    }

    setActive(option, scroll = true) {
      this.options().forEach((el) => el.setAttribute('aria-selected', String(el === option)));
      if (option) {
        this.input.setAttribute('aria-activedescendant', option.id);
        if (scroll) option.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    handleKeydown(event) {
      if (!this.enabled) return;
      const open = this.isOpen();
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!open) return;
          const list = this.options();
          if (!list.length) return;
          event.preventDefault();
          const index = list.indexOf(this.activeOption());
          const step = event.key === 'ArrowDown' ? 1 : -1;
          let next = index + step;
          if (index === -1) next = step === 1 ? 0 : list.length - 1;
          if (next < 0 || next >= list.length) this.setActive(null);
          else this.setActive(list[next]);
          break;
        }
        case 'Escape':
          if (!open) return;
          // close the list first; the next Escape closes the menu (assets/atelier-header.js)
          event.preventDefault();
          this.close();
          break;
        default:
      }
    }
  }

  customElements.define('atelier-predictive-search', AtelierPredictiveSearch);
})();
